/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { MULTIPLE_VALUE } from '../../../constants';
import { FONT_SIZE, NONE } from '../customConstants';
import {
  getPrefixStylesInSelection,
  togglePrefixStyle,
} from '../styleManipulation';
import { getVariable, isStyle } from './util';
import {
  FONT_SIZE_ATTRIBUTE,
  parseInlineFontSize,
} from '../../../elements/text/utils/mixedTypography';

const canonicalizeFontSize = (value) => `${Number(value)}`;
export const FONT_SIZE_DATA_CSS_PROPERTY = '--st-font-size-data';

export const fontSizeToStyle = (fontSize) =>
  `${FONT_SIZE}-${canonicalizeFontSize(fontSize)}`;

export const styleToFontSize = (style) =>
  parseInlineFontSize(getVariable(style, FONT_SIZE));

function elementToStyle(element) {
  if (element.tagName.toLowerCase() !== 'span') {
    return null;
  }

  const fontSize = parseInlineFontSize(
    element.getAttribute(FONT_SIZE_ATTRIBUTE)
  );
  return fontSize === null ? null : fontSizeToStyle(fontSize);
}

function getSizeStyle(styles) {
  return styles.find((style) => isStyle(style, FONT_SIZE));
}

function stylesToCSS(styles, context) {
  const style = getSizeStyle(styles);
  if (!style) {
    return null;
  }

  const fontSize = styleToFontSize(style);
  if (fontSize === null) {
    return null;
  }

  if (!context?.fontSizeToCss) {
    return null;
  }

  return {
    fontSize: context.fontSizeToCss(fontSize),
    [FONT_SIZE_DATA_CSS_PROPERTY]: canonicalizeFontSize(fontSize),
  };
}

function stylesToAttributes(styles) {
  const style = getSizeStyle(styles);
  if (!style) {
    return null;
  }

  const fontSize = styleToFontSize(style);
  if (fontSize === null) {
    return null;
  }

  return {
    [FONT_SIZE_ATTRIBUTE]: canonicalizeFontSize(fontSize),
  };
}

function getFontSize(editorState, context) {
  const sizes = new Set(
    getPrefixStylesInSelection(editorState, FONT_SIZE).map((style) =>
      style === NONE ? context?.defaultFontSize : styleToFontSize(style)
    )
  );

  return sizes.size > 1 ? MULTIPLE_VALUE : [...sizes][0];
}

function setFontSize(editorState, { fontSize, defaultFontSize } = {}) {
  const parsedFontSize = parseInlineFontSize(fontSize);
  if (parsedFontSize === null) {
    return editorState;
  }

  const shouldSetStyle = () =>
    parsedFontSize !== null && parsedFontSize !== defaultFontSize;
  const getStyleToSet = () => fontSizeToStyle(parsedFontSize);

  return togglePrefixStyle(
    editorState,
    FONT_SIZE,
    shouldSetStyle,
    getStyleToSet
  );
}

const formatter = {
  elementToStyle,
  stylesToCSS,
  stylesToAttributes,
  autoFocus: false,
  getters: {
    fontSize: getFontSize,
  },
  setters: {
    setFontSize,
  },
};

export default formatter;
